import React, { useEffect, useState, useCallback } from 'react';
import { complianceStandardsApi } from '../../services/api';
import { useSettings } from '../../context/SettingsContext';
import { Card, CardStack, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Loader2, Shield, Plus, Trash2, AlertTriangle, CheckCircle2, Clock,
  XCircle, Upload, FileX, Globe, FilePlus2,
} from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmDeleteDialog } from '../ConfirmDeleteDialog';
import { useConfirm } from '../useConfirm';

const STATUS_META = {
  valid:    { Icon: CheckCircle2,  label: 'Valid',    cls: 'lm-ok' },
  expiring: { Icon: Clock,         label: 'Expiring', cls: 'lm-warn' },
  expired:  { Icon: XCircle,       label: 'Expired',  cls: 'lm-crit' },
  missing:  { Icon: AlertTriangle, label: 'Missing',  cls: 'lm-crit' },
};

const SlotPill = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.missing;
  const { Icon, label, cls } = m;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-[3px] border px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-[0.05em] ${cls}`}
      style={{ padding: '2px 6px' }}
    >
      <Icon className="w-2.5 h-2.5" />
      {label}
    </span>
  );
};

const ScoreRing = ({ score }) => {
  const pct = Math.max(0, Math.min(100, score || 0));
  const tone = pct >= 90 ? 'lm-ring-ok' : pct >= 70 ? 'lm-ring-warn' : 'lm-ring-crit';
  return (
    <div className={`shrink-0 w-20 h-20 rounded-full border-2 flex flex-col items-center justify-center ${tone}`}>
      <div className="text-[24px] font-bold font-mono leading-none" data-testid="compliance-score">{pct}</div>
      <div className="text-[9px] uppercase tracking-[0.16em] opacity-70 mt-0.5">Score</div>
    </div>
  );
};

const AssetComplianceCard = ({ assetId, onCertificatesChanged, onNavigateToCertificates, onNavigateToStandards }) => {
  const { formatDate } = useSettings();
  const { confirm, dialogProps } = useConfirm();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [standards, setStandards] = useState([]);
  const [linking, setLinking] = useState(false);
  const [showLinker, setShowLinker] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await complianceStandardsApi.assetCompliance(assetId);
      setData(data);
    } catch {
      toast.error('Failed to load compliance');
    } finally {
      setLoading(false);
    }
  }, [assetId]);

  useEffect(() => {
    load();
  }, [load]);

  const loadStandards = async () => {
    try {
      const { data } = await complianceStandardsApi.list();
      setStandards(data || []);
    } catch {
      toast.error('Failed to load standards');
    }
  };

  const handleLink = async (standardId) => {
    setLinking(true);
    try {
      const { data: r } = await complianceStandardsApi.linkToAsset(assetId, standardId);
      toast.success(`Linked. ${r.schedules_created || 0} maintenance schedule(s) auto-created.`);
      setShowLinker(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to link standard');
    } finally {
      setLinking(false);
    }
  };

  const handleUnlink = (standardId) => {
    confirm({
      title: 'Remove this standard from the asset?',
      description: 'The auto-created maintenance schedules will remain.',
      confirmLabel: 'Remove',
      testId: 'confirm-unlink-standard',
      onConfirm: async () => {
        try {
          await complianceStandardsApi.unlinkFromAsset(assetId, standardId);
          toast.success('Standard removed');
          await load();
        } catch (e) {
          toast.error(e.response?.data?.detail || 'Failed to unlink');
          throw e;
        }
      },
    });
  };

  const handleGenerateTemplate = async (standard) => {
    try {
      const std = await complianceStandardsApi.get(standard.id);
      const inspections = std?.data?.required_inspections || [];
      const first = inspections[0]?.type || 'Inspection';
      const { data } = await complianceStandardsApi.generateTemplate(standard.id, {
        inspection_type: first,
      });
      toast.success(
        `Draft template "${data.template.name}" created — review & publish from the Templates page.`,
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate template');
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const linked = data?.linked_standards || [];
  const slots = data?.required_cert_slots || [];
  const score = data?.score || {};
  const unlinkedAvailable = standards.filter((s) => !linked.find((l) => l.id === s.id));

  return (
    <CardStack>
      {/* Score + summary */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="as-section">
            <Shield className="w-4 h-4 text-[#C9A400]" />
            Compliance posture
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6 flex-wrap">
            <ScoreRing score={score.score} />
            <div className="flex-1 min-w-[240px] grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <div data-testid="compliance-certs-breakdown">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Certificates</div>
                <div className="font-mono">{score.required_certs_valid + score.required_certs_expiring}/{score.required_certs_total || 0}</div>
                <div className="text-muted-foreground text-[10px] mt-0.5">
                  {score.required_certs_missing}m · {score.required_certs_expired}e
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Inspections</div>
                <div className="font-mono">{score.inspections_due - score.inspections_overdue}/{score.inspections_due || 0}</div>
                <div className="text-muted-foreground text-[10px] mt-0.5">
                  {score.inspections_overdue} overdue
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Defects</div>
                <div className="font-mono">{score.open_defects_high_critical}</div>
                <div className="text-muted-foreground text-[10px] mt-0.5">open high/crit</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Standards</div>
                <div className="font-mono">{linked.length}</div>
                <div className="text-muted-foreground text-[10px] mt-0.5">linked</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Linked standards */}
      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
          <CardTitle className="as-section">Linked standards</CardTitle>
          <Button
            size="sm"
            variant="outline"
            onClick={() => { setShowLinker((v) => !v); if (!standards.length) loadStandards(); }}
            data-testid="link-standard-btn"
          >
            <Plus className="w-3 h-3 mr-1" />
            Link standard
          </Button>
        </CardHeader>
        <CardContent>
          {linked.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              <Shield className="w-6 h-6 mx-auto mb-2 opacity-40" />
              No standards linked. Link a standard to track required certificates &amp; auto-schedule inspections.
              {onNavigateToStandards && (
                <div className="mt-3">
                  <Button size="sm" variant="link" onClick={onNavigateToStandards}>
                    <Globe className="w-3 h-3 mr-1" />
                    Browse the international library
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <ul className="space-y-2">
              {linked.map((s) => (
                <li
                  key={s.id}
                  className="flex items-center justify-between p-2 rounded-sm border border-border bg-[#FBFAF7]"
                  data-testid={`linked-std-${s.id}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="lm-code-badge">{s.code}</span>
                    <span className="text-sm truncate">{s.name}</span>
                    <span className="text-[10px] text-muted-foreground hidden md:inline">{s.jurisdiction}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleGenerateTemplate(s)}
                      title="Generate inspection template from this standard"
                      className="text-muted-foreground hover:text-primary p-1"
                      data-testid={`generate-template-${s.id}`}
                    >
                      <FilePlus2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleUnlink(s.id)}
                      title="Unlink standard from asset"
                      className="text-muted-foreground hover:text-red-500 p-1"
                      data-testid={`unlink-std-${s.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {/* Inline linker */}
          {showLinker && (
            <div className="mt-3 p-3 rounded-sm border border-border bg-[#FBFAF7]">
              {unlinkedAvailable.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  No more standards available. Add new ones in <em>Compliance Standards</em> or the international library.
                </p>
              ) : (
                <ul className="space-y-1 max-h-48 overflow-auto">
                  {unlinkedAvailable.map((s) => (
                    <li key={s.id} className="flex items-center justify-between gap-2 p-1.5 hover:bg-[#F2F0EA] rounded-sm">
                      <div className="flex items-center gap-2 min-w-0 text-sm">
                        <span className="lm-code-badge">{s.code}</span>
                        <span className="truncate">{s.name}</span>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={linking}
                        onClick={() => handleLink(s.id)}
                        data-testid={`link-pick-${s.id}`}
                      >
                        {linking ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Link'}
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Required cert slots */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="as-section">Required certificates</CardTitle>
        </CardHeader>
        <CardContent>
          {slots.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              <FileX className="w-6 h-6 mx-auto mb-2 opacity-40" />
              No required certificates — link a standard to populate this list.
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {slots.map((slot) => (
                <li
                  key={slot.slot_id}
                  className="py-2.5 flex items-center justify-between gap-3 flex-wrap"
                  data-testid={`cert-slot-${slot.slot_id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <SlotPill status={slot.status} />
                      <span className="font-medium text-sm">{slot.certificate_type}</span>
                      <span className="lm-code-badge">{slot.standard_code}</span>
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      {slot.interval_months ? `Renews every ${slot.interval_months} month(s)` : 'One-off (no renewal)'}
                      {slot.expiry_date && ` · Expires ${formatDate(slot.expiry_date)}`}
                      {slot.certificate_number && ` · #${slot.certificate_number}`}
                    </div>
                  </div>
                  <div>
                    {slot.status === 'missing' || slot.status === 'expired' ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onNavigateToCertificates?.(slot)}
                        data-testid={`cert-slot-upload-${slot.slot_id}`}
                      >
                        <Upload className="w-3 h-3 mr-1" />
                        {slot.status === 'expired' ? 'Renew' : 'Upload'}
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onNavigateToCertificates?.(slot)}
                        data-testid={`cert-slot-view-${slot.slot_id}`}
                      >
                        View
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
      <ConfirmDeleteDialog {...dialogProps} />
    </CardStack>
  );
};

export default AssetComplianceCard;
